import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Music,
  Calendar,
  Download,
  Clock,
  FileText,
  Sparkles,
  AlertCircle,
  TrendingUp,
  Album,
  User,
  Camera,
  ChevronLeft,
  ChevronRight,
  Maximize2
} from 'lucide-react';
import { SequenceDetail } from '@/types/sequence';
import { formatDistanceToNow, format } from 'date-fns';
import { formatDuration, formatHolidayName } from '@/utils/formatters';
import { layoutPhotoService } from '@/services/layoutPhotoService';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface SequenceMetadataProps {
  sequence: SequenceDetail;
}

const SequenceMetadata: React.FC<SequenceMetadataProps> = ({ sequence }) => {
  const song = sequence.song;
  const hasSpotifyData = !!(song?.spotifyTrackId || song?.album || song?.spotifyPopularity);
  const [layoutPhotos, setLayoutPhotos] = useState<any[]>([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  // Fetch layout photos
  useEffect(() => {
    const fetchLayoutPhotos = async () => {
      try {
        const photos = await layoutPhotoService.getLayoutPhotos(sequence.id);
        if (photos.length > 0) {
          setLayoutPhotos(photos);
        }
      } catch (error) {
        // Silently handle error - photos are optional
      }
    };

    fetchLayoutPhotos();
  }, [sequence.id]);

  const handleNextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % layoutPhotos.length);
  };

  const handlePrevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + layoutPhotos.length) % layoutPhotos.length);
  };
  
  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Sequence Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Song Information with Album Art */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Song Information
          </h4>
          
          <div className="flex gap-4">
            {(song?.imageUrl || sequence.album_cover_url) && (
              <div className="shrink-0">
                <img 
                  src={song?.imageUrl || sequence.album_cover_url} 
                  alt={`${song?.album || song?.title || sequence.title} cover`}
                  className="w-32 h-32 rounded-md object-cover shadow-xs"
                />
              </div>
            )}
            
            <div className="flex-1 space-y-1">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-sm">{song?.title || sequence.title}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {song?.artist || sequence.artist || 'Unknown Artist'}
                  </p>
                </div>
                {song?.spotifyExplicit && (
                  <Badge variant="secondary" className="text-xs">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Explicit
                  </Badge>
                )}
              </div>
              
              {(song?.album || sequence.album) && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Album className="h-3 w-3" />
                  {song?.album || sequence.album}
                </p>
              )}
              
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDuration(song?.duration || sequence.duration, song?.spotifyDurationMs || sequence.spotify_duration_ms)}
                </span>
                {song?.genre && (
                  <span className="flex items-center gap-1">
                    <Music className="h-3 w-3" />
                    {song.genre}
                  </span>
                )}
                {song?.spotifyPopularity && (
                  <span className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    {song.spotifyPopularity}% popular
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Technical Details */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Lighting Nerd Info
          </h4>
          
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-muted-foreground text-xs">Software</p>
              <Badge variant="outline" className="mt-1 border-2">
                {sequence.software}
              </Badge>
            </div>
            
            <div>
              <p className="text-muted-foreground text-xs">Holiday</p>
              <Badge variant="outline" className="mt-1 border-2">
                {formatHolidayName(sequence.holiday)}
              </Badge>
            </div>
            
            {sequence.channelCount && (
              <div>
                <p className="text-muted-foreground text-xs">Channels</p>
                <p className="font-medium">{sequence.channelCount.toLocaleString()}</p>
              </div>
            )}
            
            <div>
              <p className="text-muted-foreground text-xs">Duration</p>
              <p className="font-medium">{formatDuration(sequence.duration, sequence.spotify_duration_ms)}</p>
            </div>
            
            <div>
              <p className="text-muted-foreground text-xs">Files Included</p>
              <p className="font-medium">{sequence.file_type || 'Sequence file'}</p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Statistics */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Statistics
          </h4>
          
          <div className="space-y-2 text-sm">
            {sequence.downloadCount > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Download className="h-3 w-3" />
                  Downloads
                </span>
                <span className="font-medium">{sequence.downloadCount.toLocaleString()}</span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Created
              </span>
              <span className="font-medium">
                {format(new Date(sequence.createdAt), 'MMM d, yyyy')}
              </span>
            </div>
            
            {sequence.updatedAt && sequence.updatedAt !== sequence.createdAt && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Last Updated
                </span>
                <span className="font-medium">
                  {formatDistanceToNow(new Date(sequence.updatedAt), { addSuffix: true })}
                </span>
              </div>
            )}
            
            {sequence.featured && (
              <div className="pt-2">
                <Badge variant="default" className="w-full justify-center">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Featured Sequence
                </Badge>
              </div>
            )}
          </div>
        </div>

        {/* Layout Photos Section */}
        {layoutPhotos.length > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <Camera className="h-4 w-4" />
                Layout Photos ({layoutPhotos.length})
              </h4>

              <div className="space-y-3">
                {/* Main photo display */}
                <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={layoutPhotos[currentPhotoIndex].photo_url}
                    alt={`Layout photo ${currentPhotoIndex + 1}`}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() => setIsPhotoModalOpen(true)}
                  />

                  {/* Navigation buttons */}
                  {layoutPhotos.length > 1 && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/50 backdrop-blur-xs hover:bg-black/70 text-white"
                        onClick={handlePrevPhoto}
                      >
                        <ChevronLeft size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/50 backdrop-blur-xs hover:bg-black/70 text-white"
                        onClick={handleNextPhoto}
                      >
                        <ChevronRight size={16} />
                      </Button>
                    </>
                  )}

                  {/* Expand button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/50 backdrop-blur-xs hover:bg-black/70 text-white"
                    onClick={() => setIsPhotoModalOpen(true)}
                  >
                    <Maximize2 size={16} />
                  </Button>

                  {/* Caption */}
                  {layoutPhotos[currentPhotoIndex].caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <p className="text-white text-sm">{layoutPhotos[currentPhotoIndex].caption}</p>
                    </div>
                  )}
                </div>

                {/* Thumbnail strip */}
                {layoutPhotos.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto">
                    {layoutPhotos.map((photo, index) => (
                      <button
                        key={photo.id}
                        onClick={() => setCurrentPhotoIndex(index)}
                        className={cn(
                          "relative shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-all",
                          index === currentPhotoIndex
                            ? "border-primary ring-2 ring-primary/20"
                            : "border-transparent hover:border-gray-300"
                        )}
                      >
                        <img
                          src={photo.photo_url}
                          alt={`Layout thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {photo.caption && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                            <Camera className="h-4 w-4 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SequenceMetadata;